import React, { useEffect, useState } from 'react';
import { API_BASE, apiFetch, showAppConfirm } from './api';
import { theme } from './theme';

const LICENSE_CLASSES = ['B', 'C', 'C1', 'BE', 'C1E', 'CE'];

const emptyForm = { name: '', required_license_class: '', included_license_classes: [], recommended_gate_ids: [] };

const styles = {
  stack: { display: 'grid', gap: '20px' },
  card: { ...theme.glassCard, padding: '24px', color: 'var(--board-text-strong, #fff)' },
  title: { margin: '0 0 16px', color: 'var(--board-text-strong, #fff)', fontSize: '20px' },
  formGrid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(190px, 1fr))', gap: '10px', alignItems: 'start' },
  input: { ...theme.input, width: '100%' },
  select: { ...theme.select, width: '100%' },
  button: { ...theme.button, ...theme.primaryButton },
  tableWrap: { overflowX: 'auto' },
  table: theme.table,
  th: theme.th,
  td: theme.td,
  deleteBtn: { ...theme.button, ...theme.dangerButton, padding: '7px 12px', fontSize: '12px', marginRight: '6px' },
  editBtn: { ...theme.button, ...theme.warningButton, padding: '7px 12px', fontSize: '12px', marginRight: '6px' },
  saveBtn: { ...theme.button, ...theme.successButton, padding: '7px 12px', fontSize: '12px', marginRight: '6px' },
  mutedBtn: { ...theme.button, ...theme.mutedButton, padding: '7px 12px', fontSize: '12px', marginRight: '6px' },
  label: { display: 'block', marginBottom: '6px', fontSize: '12px', color: 'var(--board-text-muted, rgba(255,255,255,0.62))' },
  includeWrap: { display: 'flex', flexWrap: 'wrap', gap: '8px' },
  chip: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '6px',
    padding: '7px 10px',
    borderRadius: '999px',
    background: 'var(--board-surface-soft, rgba(255,255,255,0.08))',
    border: '1px solid var(--board-border, rgba(255,255,255,0.15))',
    fontSize: '13px',
  },
  badge: {
    display: 'inline-block',
    marginRight: '5px',
    marginBottom: '4px',
    padding: '4px 8px',
    borderRadius: '999px',
    background: 'rgba(244,63,94,0.18)',
    border: '1px solid rgba(244,63,94,0.34)',
    color: 'var(--board-danger-text, #ffe4e6)',
    fontSize: '12px',
    fontWeight: 800,
  },
  activeBadge: {
    display: 'inline-block',
    padding: '5px 9px',
    borderRadius: '999px',
    background: 'rgba(34,197,94,0.18)',
    border: '1px solid rgba(74,222,128,0.36)',
    color: 'var(--board-success-text, #dcfce7)',
    fontSize: '12px',
    fontWeight: 800,
  },
  inactiveBadge: {
    display: 'inline-block',
    padding: '5px 9px',
    borderRadius: '999px',
    background: 'rgba(148,163,184,0.16)',
    border: '1px solid rgba(148,163,184,0.34)',
    color: 'var(--board-text-soft, #e2e8f0)',
    fontSize: '12px',
    fontWeight: 800,
  },
  activeToggle: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '8px',
    color: 'var(--board-text-soft, rgba(255,255,255,0.82))',
    fontSize: '13px',
    fontWeight: 700,
  },
  inactiveRow: { opacity: 0.62 },
};

function normalizeLicenseClasses(classes) {
  return Array.isArray(classes)
    ? classes.map(item => String(item).trim()).filter(item => LICENSE_CLASSES.includes(item))
    : [];
}

function normalizeGateIds(gateIds) {
  return Array.isArray(gateIds)
    ? gateIds.map(item => Number(item)).filter(Number.isInteger)
    : [];
}

function VehicleClasses() {
  const [classes, setClasses] = useState([]);
  const [gates, setGates] = useState([]);
  const [form, setForm] = useState(emptyForm);
  const [editId, setEditId] = useState(null);
  const [editForm, setEditForm] = useState(emptyForm);
  const [viewportWidth, setViewportWidth] = useState(() => window.innerWidth);

  const loadClasses = () => {
    apiFetch(`${API_BASE}/vehicle-classes?includeInactive=true`)
      .then(res => res.json())
      .then(data => setClasses(Array.isArray(data) ? data : []));
  };

  const loadGates = () => {
    apiFetch(`${API_BASE}/gates`)
      .then(res => res.json())
      .then(data => setGates(Array.isArray(data) ? data : []));
  };

  useEffect(() => {
    loadClasses();
    loadGates();
  }, []);
  useEffect(() => {
    const handleResize = () => setViewportWidth(window.innerWidth);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const toggleIncludedLicense = (state, setter, licenseClass) => {
    const licenses = normalizeLicenseClasses(state.included_license_classes);
    setter({
      ...state,
      included_license_classes: licenses.includes(licenseClass)
        ? licenses.filter(item => item !== licenseClass)
        : [...licenses, licenseClass],
    });
  };

  const toggleRecommendedGate = (state, setter, gateId) => {
    const selectedGateIds = normalizeGateIds(state.recommended_gate_ids);
    setter({
      ...state,
      recommended_gate_ids: selectedGateIds.includes(gateId)
        ? selectedGateIds.filter(item => item !== gateId)
        : [...selectedGateIds, gateId],
    });
  };

  const saveClass = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) return;

    await apiFetch(`${API_BASE}/vehicle-classes`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(form),
    });

    setForm(emptyForm);
    loadClasses();
  };

  const startEdit = (vehicleClass) => {
    setEditId(vehicleClass.id);
    setEditForm({
      name: vehicleClass.name || '',
      required_license_class: vehicleClass.required_license_class || '',
      included_license_classes: normalizeLicenseClasses(vehicleClass.included_license_classes),
      recommended_gate_ids: normalizeGateIds(vehicleClass.recommended_gate_ids),
      is_active: vehicleClass.is_active !== false,
    });
  };

  const saveEdit = async (id) => {
    await apiFetch(`${API_BASE}/vehicle-classes/${id}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(editForm),
    });

    setEditId(null);
    loadClasses();
  };

  const deleteClass = async (id) => {
    if (!await showAppConfirm('Fahrzeugklasse wirklich löschen?', {
      title: 'Fahrzeugklasse löschen',
      confirmLabel: 'Löschen',
      danger: true,
    })) return;
    await apiFetch(`${API_BASE}/vehicle-classes/${id}`, { method: 'DELETE' });
    loadClasses();
  };

  const renderIncludedLicenses = (state, setter) => {
    const licenses = normalizeLicenseClasses(state.included_license_classes);

    return (
      <div>
        <span style={styles.label}>Weitere Führerscheine erlaubt</span>
        <div style={styles.includeWrap}>
          {LICENSE_CLASSES.map(licenseClass => (
            <label key={licenseClass} style={styles.chip}>
              <input
                type="checkbox"
                checked={licenses.includes(licenseClass)}
                onChange={() => toggleIncludedLicense(state, setter, licenseClass)}
              />
              {licenseClass}
            </label>
          ))}
        </div>
      </div>
    );
  };

  const renderRecommendedGates = (state, setter) => {
    const selectedGateIds = normalizeGateIds(state.recommended_gate_ids);

    return (
      <div>
        <span style={styles.label}>Empfohlene Tore</span>
        <div style={styles.includeWrap}>
          {gates.length === 0 ? (
            <span style={{ color: 'var(--board-text-muted, rgba(255,255,255,0.58))', fontSize: '13px' }}>Keine Tore vorhanden</span>
          ) : gates.map(gate => (
            <label key={gate.id} style={styles.chip}>
              <input
                type="checkbox"
                checked={selectedGateIds.includes(Number(gate.id))}
                onChange={() => toggleRecommendedGate(state, setter, Number(gate.id))}
              />
              {gate.name}
            </label>
          ))}
        </div>
      </div>
    );
  };

  const renderLicenseSelect = (state, setter) => (
    <div>
      <span style={styles.label}>Benötigter Führerschein</span>
      <select
        style={styles.select}
        value={state.required_license_class}
        onChange={e => setter({ ...state, required_license_class: e.target.value })}
      >
        <option value="">- keiner -</option>
        {LICENSE_CLASSES.map(licenseClass => (
          <option key={licenseClass} value={licenseClass}>{licenseClass}</option>
        ))}
      </select>
    </div>
  );

  const isMobile = viewportWidth < 760;

  return (
    <div style={styles.stack}>
      <div style={styles.card}>
        <h2 style={styles.title}>Fahrzeugklasse anlegen</h2>
        <form style={isMobile ? { ...styles.formGrid, gridTemplateColumns: '1fr' } : styles.formGrid} onSubmit={saveClass}>
          <input
            style={styles.input}
            placeholder="Name *"
            value={form.name}
            onChange={e => setForm({ ...form, name: e.target.value })}
            required
          />
          {renderLicenseSelect(form, setForm)}
          {renderIncludedLicenses(form, setForm)}
          {renderRecommendedGates(form, setForm)}
          <button style={styles.button} type="submit">Hinzufügen</button>
        </form>
      </div>

      <div style={styles.card}>
        <h2 style={styles.title}>Fahrzeugklassen ({classes.length})</h2>
        <div style={styles.tableWrap}>
          <table style={styles.table}>
            <thead>
              <tr>
                <th style={styles.th}>ID</th>
                <th style={styles.th}>Name</th>
                <th style={styles.th}>Führerschein</th>
                <th style={styles.th}>Weitere erlaubt</th>
                <th style={styles.th}>Empfohlene Tore</th>
                <th style={styles.th}>Status</th>
                <th style={styles.th}>Aktion</th>
              </tr>
            </thead>
            <tbody>
              {classes.map(vehicleClass => {
                const includedLicenses = normalizeLicenseClasses(vehicleClass.included_license_classes);
                const recommendedGateIds = normalizeGateIds(vehicleClass.recommended_gate_ids);
                const recommendedGates = gates.filter(gate => recommendedGateIds.includes(Number(gate.id)));
                return (
                  <tr key={vehicleClass.id} style={vehicleClass.is_active === false ? styles.inactiveRow : undefined}>
                    <td style={styles.td}>{vehicleClass.id}</td>
                    {editId === vehicleClass.id ? (
                      <>
                        <td style={styles.td}>
                          <input style={styles.input} value={editForm.name} onChange={e => setEditForm({ ...editForm, name: e.target.value })} />
                        </td>
                        <td style={styles.td}>{renderLicenseSelect(editForm, setEditForm)}</td>
                        <td style={styles.td}>{renderIncludedLicenses(editForm, setEditForm)}</td>
                        <td style={styles.td}>{renderRecommendedGates(editForm, setEditForm)}</td>
                        <td style={styles.td}>
                          <label style={styles.activeToggle}>
                            <input
                              type="checkbox"
                              checked={editForm.is_active !== false}
                              onChange={e => setEditForm({ ...editForm, is_active: e.target.checked })}
                            />
                            Aktiv
                          </label>
                        </td>
                      </>
                    ) : (
                      <>
                        <td style={styles.td}><strong>{vehicleClass.name}</strong></td>
                        <td style={styles.td}>{vehicleClass.required_license_class || '-'}</td>
                        <td style={styles.td}>
                          {includedLicenses.length > 0
                            ? includedLicenses.map(item => <span key={item} style={styles.badge}>{item}</span>)
                            : '-'}
                        </td>
                        <td style={styles.td}>
                          {recommendedGates.length > 0
                            ? recommendedGates.map(gate => <span key={gate.id} style={styles.badge}>{gate.name}</span>)
                            : '-'}
                        </td>
                        <td style={styles.td}>
                          <span style={vehicleClass.is_active === false ? styles.inactiveBadge : styles.activeBadge}>
                            {vehicleClass.is_active === false ? 'Inaktiv' : 'Aktiv'}
                          </span>
                        </td>
                      </>
                    )}
                    <td style={styles.td}>
                      {editId === vehicleClass.id ? (
                        <>
                          <button style={styles.saveBtn} onClick={() => saveEdit(vehicleClass.id)}>Speichern</button>
                          <button style={styles.mutedBtn} onClick={() => setEditId(null)}>Abbrechen</button>
                        </>
                      ) : (
                        <>
                          <button style={styles.editBtn} onClick={() => startEdit(vehicleClass)}>Bearbeiten</button>
                          <button style={styles.deleteBtn} onClick={() => deleteClass(vehicleClass.id)}>Löschen</button>
                        </>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

export default VehicleClasses;
